'use client';

import { useAuth } from '@/hooks/useAuth';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

// Marketing sections
import { Header } from '@/components/marketing/sections/Header';
import { Hero } from '@/components/marketing/sections/Hero';
import { HowItWorks } from '@/components/marketing/sections/HowItWorks';
import { Testimonials } from '@/components/marketing/sections/Testimonials';
import { ValueProp } from '@/components/marketing/sections/ValueProp';
import { Pricing } from '@/components/marketing/sections/Pricing';
import { BookPreview } from '@/components/marketing/sections/BookPreview';
import { ArtGallery } from '@/components/marketing/sections/ArtGallery';
import { Footer } from '@/components/marketing/sections/Footer';

/**
 * TinyChapters Marketing Landing Page
 *
 * A warm, elegant marketing website that tells the TinyChapters story
 * and converts visitors into users.
 *
 * Design: Warm cream/terracotta palette with serif headlines
 *
 * Sections:
 * 1. Header - Sticky navigation
 * 2. Hero - Main value proposition with device mockup
 * 3. How It Works - Camera, Cloud, Book steps
 * 4. Value Prop - A Modern Keepsake section
 * 5. Testimonials - Rolling carousel
 * 6. Pricing - Free app + paid books
 * 7. Book Preview - Interactive sample storybook
 * 8. Footer - Multi-column links
 */
export default function Home() {
  const { user, loading } = useAuth();
  const router = useRouter();

  // Redirect logged-in users to their gallery
  useEffect(() => {
    if (!loading && user) {
      router.push('/gallery');
    }
  }, [user, loading, router]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-200">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-terracotta-500" />
      </div>
    );
  }

  // Show loading while redirecting logged-in users
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-200">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-terracotta-500" />
      </div>
    );
  }

  return (
    <>
      <Header />
      <main>
        <Hero />
        <HowItWorks />
        <ValueProp />
        <Testimonials />
        <Pricing />
        <BookPreview />
        <ArtGallery />
      </main>
      <Footer />
    </>
  );
}
